"use client";

import type { DeviceChannelDto, TelemetryAggregatePointDto, TelemetryDto } from "@/lib/api/types";
import type { JsonObject } from "@/lib/api/types/json";

export function coerceBooleanTelemetryValue(value: unknown): boolean | null {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    if (value === 1) return true;
    if (value === 0) return false;
    return null;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["1", "true", "on", "open", "opened", "yes", "active", "running"].includes(normalized)) {
      return true;
    }
    if (["0", "false", "off", "close", "closed", "no", "inactive", "stopped"].includes(normalized)) {
      return false;
    }
  }
  return null;
}

export function formatTelemetryValue(value: unknown): string {
  if (typeof value === "number" && Number.isFinite(value)) {
    if (Math.abs(value) >= 100) return value.toFixed(0);
    if (Math.abs(value) >= 10) return value.toFixed(1);
    return value.toFixed(2);
  }
  if (typeof value === "boolean") {
    return value ? "True" : "False";
  }
  if (typeof value === "string" && value.trim()) {
    return value;
  }
  return "--";
}

export function formatBooleanState(value: unknown): string {
  const normalized = coerceBooleanTelemetryValue(value);
  if (normalized == null) {
    return formatTelemetryValue(value);
  }
  return normalized ? "Open / True" : "Closed / False";
}

export function getTelemetryEntries(data: JsonObject | null | undefined): Array<[string, unknown]> {
  if (!data) return [];
  return Object.entries(data).filter(([, value]) => value !== null && value !== undefined);
}

export function getLatestTelemetryRow(rows: TelemetryDto[]): TelemetryDto | null {
  if (rows.length === 0) return null;
  return rows[rows.length - 1] ?? null;
}

export function formatRelativeTime(iso: string | null | undefined, nowMs: number = Date.now()): string {
  if (!iso) return "No data";
  const timestamp = new Date(iso).getTime();
  if (Number.isNaN(timestamp)) return iso;
  const deltaSeconds = Math.max(0, Math.round((nowMs - timestamp) / 1000));
  if (deltaSeconds < 60) return `${deltaSeconds}s ago`;
  const minutes = Math.floor(deltaSeconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function channelLabelMap(channels: DeviceChannelDto[] | undefined): Record<string, string> {
  return Object.fromEntries((channels ?? []).map((channel) => [channel.key, getChannelDisplayLabel(channel)]));
}

export function channelUnitMap(channels: DeviceChannelDto[] | undefined): Record<string, string> {
  return Object.fromEntries((channels ?? []).map((channel) => [channel.key, channel.unit ?? ""]));
}

export function getChannelDisplayLabel(channel: Pick<DeviceChannelDto, "key" | "label">): string {
  return channel.label?.trim() || channel.key;
}

export function summarizeMetric(points: TelemetryAggregatePointDto[], metricKey: string) {
  const values = points
    .map((point) => point.values[metricKey])
    .filter((value): value is number => typeof value === "number" && Number.isFinite(value));

  if (values.length === 0) {
    return {
      latest: null,
      min: null,
      max: null,
      average: null,
    };
  }

  const latest = values[values.length - 1] ?? null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const average = values.reduce((sum, value) => sum + value, 0) / values.length;

  return { latest, min, max, average };
}
